// A seller types an amount in the currency its field shows, and the form keeps
// it as typed text until it saves. That currency can change under the text:
// the seller's currency had no rate yet, so the field showed AUD, and then the
// rate arrived (watchConversionRates in src/lib/currencies.ts); or they picked
// another currency in another tab. Converted at the new rate, "20" typed as
// A$20 is saved as 20 US dollars, about A$30. So a form records which currency
// its amounts are in (their denomination) and restates them, at the same AUD
// value, whenever that changes (useHeldDenomination in
// src/hooks/use-held-denomination.ts).
//
// Pure: no React, no theme. Covered by amount-denomination.test.ts.

/** Stored amounts are AUD. Spelled here rather than imported from the client-only theme context. */
const BASE = 'AUD';

/**
 * The currency typed amounts are in, with its rate as the theme converts them:
 * units of `currency` per A$1, all-in, i.e. toDisplayAmount(1). Dividing a
 * typed amount by it is exactly what toBaseAmount does.
 */
export type AmountDenomination = { readonly currency: string; readonly rate: number };

const BASE_DENOMINATION: AmountDenomination = { currency: BASE, rate: 1 };

const ISO_CODE = /^[A-Z]{3}$/;

function usableRate(rate: unknown): rate is number {
  return typeof rate === 'number' && Number.isFinite(rate) && rate > 0;
}

/**
 * The denomination the theme converts typed amounts in right now. Pass its
 * `displayCurrency` (AUD whenever it has no rate for the viewer's pick, never
 * the raw pick) and its `toDisplayAmount`.
 */
export function currentDenomination(
  displayCurrency: string,
  toDisplayAmount: (aud: number) => number,
): AmountDenomination {
  if (displayCurrency === BASE) return BASE_DENOMINATION;
  const rate = toDisplayAmount(1);
  return ISO_CODE.test(displayCurrency) && usableRate(rate) ? { currency: displayCurrency, rate } : BASE_DENOMINATION;
}

/**
 * A denomination a form's draft recorded, or null: a draft saved before drafts
 * recorded one, or one edited by hand.
 */
export function readDenomination(value: unknown): AmountDenomination | null {
  if (!value || typeof value !== 'object') return null;
  const { currency, rate } = value as { currency?: unknown; rate?: unknown };
  if (typeof currency !== 'string' || !ISO_CODE.test(currency) || !usableRate(rate)) return null;
  if (currency === BASE && rate !== 1) return null;
  return { currency, rate };
}

/** AUD bounds an amount that was inside them should stay inside when restated. */
export type AudBounds = { readonly minAud?: number; readonly maxAud?: number };

/**
 * `amount`, typed in `from`, restated in `to` at the same AUD value: what the
 * field should hold once the currency it is typed in changes under it.
 *
 * Two decimals, the nearest the new currency can say it in. With `bounds`, an
 * amount that was inside them stays inside: A$1.00 is US$0.663, and the
 * nearest cent, US$0.66, is A$0.9955, under the A$1 floor, so it becomes
 * US$0.67 instead.
 *
 * Comes back unchanged: an empty, zero or unreadable amount; any amount when
 * the currency is the same (a rate that merely moved changes nothing the
 * seller typed); and any amount when either rate is unusable.
 */
export function redenominateAmount<T extends string | number>(
  amount: T,
  from: AmountDenomination,
  to: AmountDenomination,
  bounds?: AudBounds,
): T | string {
  if (from.currency === to.currency || !usableRate(from.rate) || !usableRate(to.rate)) return amount;
  const typed = typeof amount === 'number' ? amount : Number(String(amount).trim());
  if (!Number.isFinite(typed) || typed <= 0) return amount;
  const aud = typed / from.rate;
  let cents = Math.round(aud * to.rate * 100);
  // What the field will be read back as: the theme's toBaseAmount of it.
  const audOf = (c: number) => c / 100 / to.rate;
  if (bounds?.minAud !== undefined && aud >= bounds.minAud && audOf(cents) < bounds.minAud) cents += 1;
  if (bounds?.maxAud !== undefined && aud <= bounds.maxAud && audOf(cents) > bounds.maxAud) cents -= 1;
  return (cents / 100).toFixed(2);
}
